import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconBox} from '../../../../libs/shared/ui/src/components/iconBox/iconBox';
import {useLinkNavigation} from '../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

export type EducationBannerContentItem = {
  content: ReactNode;
  key: string;
};

export interface EducationBannerProps {
  educationBannerTitle: ReactNode;
  leftButton?: ReactNode;
  learnMoreUrl?: string;
  onCloseEducationBanner: () => void;
  /** Renders the content in a list */
  educationBannerContent: ReadonlyArray<EducationBannerContentItem>;
  className?: string;
}

/*
 * Style.
 */

const StyledIconBoxContentDiv = styled.div`
  display: flex;
  flex-flow: column;
  padding-top: 1px;
`;

const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.small};
`;

const StyledEducationListUl = styled.ul`
  margin-left: -28px;
  padding-left: 21px;
  margin-top: 8px;
  margin-bottom: 16px;
`;

const StyledEducationListItemLi = styled.li`
  font-size: ${fontSizes.verySmall};
  padding-left: 8px;

  &:not(:last-child) {
    margin-bottom: 8px;
  }
`;

const StyledActionsContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-left: -10px;
`;

const StyledLearnMoreSpan = styled.span`
  text-decoration: underline;
`;

/*
 * Component.
 */

export const EducationBanner: FC<EducationBannerProps> = (props) => {
  const theme = useUserTheme();
  const {navigateToLink} = useLinkNavigation();
  const {educationBannerTitle, learnMoreUrl, leftButton, onCloseEducationBanner, educationBannerContent} =
    props;

  return (
    <IconBox
      className={props.className}
      iconName="infoFilledSmall"
      iconSize={16}
      iconColor={theme.palette.blue.shade40}
      borderRadius={16}
      onRequestClose={onCloseEducationBanner}
    >
      <StyledIconBoxContentDiv>
        <StyledTitleDiv>{educationBannerTitle}</StyledTitleDiv>
        <StyledEducationListUl>
          {educationBannerContent.map(({content, key}) => (
            <StyledEducationListItemLi key={key}>{content}</StyledEducationListItemLi>
          ))}
        </StyledEducationListUl>

        {(learnMoreUrl || leftButton) && (
          <StyledActionsContainerDiv>
            {leftButton}
            {learnMoreUrl && (
              <Button
                size={VisualSizesEnum.SMALL}
                rightIcon="externalLinkSmall"
                variant={ButtonVariantsEnum.LINK}
                onClick={() => navigateToLink(learnMoreUrl)}
              >
                <StyledLearnMoreSpan>
                  <Trans id="education-banner-learn-more-link">Learn more</Trans>
                </StyledLearnMoreSpan>
              </Button>
            )}
          </StyledActionsContainerDiv>
        )}
      </StyledIconBoxContentDiv>
    </IconBox>
  );
};
